import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  LayoutDashboard, Users, Building2, Settings,
  FileText, Play, LogOut, User, ChevronRight, ListChecks, Database, Eraser, Regex, Layers, Link2, Wand2, Bookmark, BarChart3, CreditCard, Tag, ShieldCheck, Clock, Bell, Webhook
} from "lucide-react";

const SUPER_NAV = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/rule-builder", icon: ListChecks, label: "New Rule Set" },
  { to: "/transform-sets", icon: Wand2, label: "Transform Sets" },
  { to: "/file-templates", icon: Bookmark, label: "File Templates" },
  { to: "/reports", icon: BarChart3, label: "Reports" },
  { to: "/lookups", icon: Database, label: "Lookup Tables" },
  { to: "/cleanups", icon: Eraser, label: "Cleanup Tables" },
  { to: "/regex", icon: Regex, label: "Regex Patterns" },
  { to: "/admin/superadmins", icon: ShieldCheck, label: "Super Admins" },
  { to: "/admin/plans", icon: Tag, label: "Pricing Plans" },
  { to: "/admin/settings", icon: Settings, label: "App Settings" },
  { to: "/schedules", icon: Clock, label: "Schedules" },
  { to: "/webhooks", icon: Webhook, label: "Webhooks" },
  { to: "/profile", icon: User, label: "Profile" },
  { to: "/notifications", icon: Bell, label: "Notifications" },
];

const DEPT_ADMIN_NAV = [
  { to: "/dept-admin", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/reconcile", icon: Play, label: "New Reconciliation" },
  { to: "/multi-reconcile", icon: Layers, label: "Multi-file Recon" },
  { to: "/chain-reconcile", icon: Link2, label: "Chain Reconciliation" },
  { to: "/chain-templates", icon: Bookmark, label: "Chain Templates" },
  { to: "/schedules", icon: Clock, label: "Schedules" },
  { to: "/webhooks", icon: Webhook, label: "Webhooks" },
  { to: "/rule-builder", icon: ListChecks, label: "New Rule Set" },
  { to: "/transform-sets", icon: Wand2, label: "Transform Sets" },
  { to: "/file-templates", icon: Bookmark, label: "File Templates" },
  { to: "/reports", icon: BarChart3, label: "Reports" },
  { to: "/lookups", icon: Database, label: "Lookup Tables" },
  { to: "/cleanups", icon: Eraser, label: "Cleanup Tables" },
  { to: "/regex", icon: Regex, label: "Regex Patterns" },
  { to: "/profile", icon: User, label: "Profile" },
  { to: "/notifications", icon: Bell, label: "Notifications" },
];

const USER_NAV = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/reconcile", icon: Play, label: "New Reconciliation" },
  { to: "/multi-reconcile", icon: Layers, label: "Multi-file Recon" },
  { to: "/chain-reconcile", icon: Link2, label: "Chain Reconciliation" },
  { to: "/chain-templates", icon: Bookmark, label: "Chain Templates" },
  { to: "/schedules", icon: Clock, label: "Schedules" },
  { to: "/reports", icon: BarChart3, label: "Reports" },
  { to: "/transform-sets", icon: Wand2, label: "Transform Sets", readonly: true },
  { to: "/file-templates", icon: Bookmark, label: "File Templates", readonly: true },
  { to: "/lookups", icon: Database, label: "Lookup Tables", readonly: true },
  { to: "/cleanups", icon: Eraser, label: "Cleanup Tables", readonly: true },
  { to: "/regex", icon: Regex, label: "Regex Patterns", readonly: true },
  { to: "/profile", icon: User, label: "Profile" },
  { to: "/notifications", icon: Bell, label: "Notifications" },
];

function NavItem({ to, icon: Icon, label, end, readonly }) {
  return (
    <NavLink to={to} end={end}
      className={({ isActive }) =>
        `sidebar-item ${isActive ? "active" : ""}`
      }
    >
      <Icon size={16} strokeWidth={1.5} />
      <span>{label}</span>
      {readonly && (
        <span title="Read-only for your role" style={{ marginLeft: "auto", fontSize: "0.58rem", padding: "0.08rem 0.4rem", borderRadius: 999, background: "#F3F4F6", color: "#6B7280", border: "1px solid #E5E7EB", fontWeight: 600, letterSpacing: "0.04em" }}>
          VIEW
        </span>
      )}
    </NavLink>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const navItems =
    user?.role === "superadmin" ? SUPER_NAV :
    user?.role === "dept_admin" ? DEPT_ADMIN_NAV : USER_NAV;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const roleLabel =
    user?.role === "superadmin" ? "Super Admin" :
    user?.role === "dept_admin" ? "Dept Admin" : "Analyst";

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "var(--bg-default)" }}>
      {/* Sidebar — fixed; main content is offset via marginLeft */}
      <aside className="sidebar" style={{ display: "flex", flexDirection: "column" }}>
        {/* Logo */}
        <div style={{ padding: "1.5rem 1.25rem 1rem", borderBottom: "1px solid var(--border-default)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <div style={{
              width: "2rem", height: "2rem", borderRadius: "0.5rem",
              background: "var(--sage)", display: "flex", alignItems: "center",
              justifyContent: "center"
            }}>
              <span style={{ color: "white", fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 700, fontSize: "0.875rem" }}>R</span>
            </div>
            <span style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 700, fontSize: "1.125rem", color: "var(--text-primary)" }}>
              ReconX
            </span>
          </div>
        </div>

        {/* Navigation */}
        <nav style={{ flex: 1, paddingTop: "0.75rem" }}>
          {navItems.map(item => (
            <NavItem key={item.to} {...item} />
          ))}
        </nav>

        {/* User info + logout */}
        <div style={{ padding: "1rem", borderTop: "1px solid var(--border-default)" }}>
          <div style={{ padding: "0.625rem 0.5rem", marginBottom: "0.5rem" }}>
            <p style={{ fontSize: "0.875rem", fontWeight: 600, color: "var(--text-primary)", margin: 0 }}>{user?.name}</p>
            <p style={{ fontSize: "0.75rem", color: "var(--text-muted)", margin: 0 }}>{roleLabel}</p>
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="sidebar-item"
            style={{ width: "100%", border: "none", background: "none", cursor: "pointer", justifyContent: "flex-start" }}
          >
            <LogOut size={16} strokeWidth={1.5} />
            <span>Log out</span>
          </button>
        </div>
      </aside>

      {/* Main content — offset by sidebar width */}
      <main style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: "100vh", marginLeft: 240 }}>
        {/* Top bar */}
        <header style={{
          background: "rgba(245,244,239,0.85)", backdropFilter: "blur(12px)",
          borderBottom: "1px solid var(--border-default)",
          padding: "0 2rem", height: "3.5rem",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          position: "sticky", top: 0, zIndex: 10
        }}>
          <div />
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <div style={{
              width: "2rem", height: "2rem", borderRadius: "9999px",
              background: "var(--sage-light)", border: "1px solid var(--sage-border)",
              display: "flex", alignItems: "center", justifyContent: "center"
            }}>
              <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--sage)" }}>
                {(user?.name || "U")[0].toUpperCase()}
              </span>
            </div>
            <span style={{ fontSize: "0.875rem", color: "var(--text-secondary)" }}>{user?.email}</span>
          </div>
        </header>
        <div style={{ flex: 1, padding: "2rem" }}>
          {children}
        </div>
      </main>
    </div>
  );
}
